import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import API from 'api/index.js';
import { APP } from 'constants.js';
import { getParsedLSItemOr, parseJWTFromAccessToken } from 'utils.js';

const parsed = parseJWTFromAccessToken();
const { first_name, last_name, groups } = parsed?.user || {};

const initialState = {
    firstName: '',
    lastName: '',
    groups: [],
    isAuthenticated: false,
    isBatteryProvider: false,
    isBatteryReceiver: false,
    isContractor: false,
    isEmployee: false,
    isOperator: false,
    isPerson: false,
    isRecycler: false,
    isImporter: false,
    app: getParsedLSItemOr('app', APP.UNAUTHENTICATED, true),
    isExpired: false,
    ...(parsed ? {
        isAuthenticated: true,
        firstName: first_name,
        lastName: last_name,
        groups,
        isBatteryProvider: groups.includes('provider'),
        isBatteryReceiver: groups.includes('receiver'),
        isContractor: groups.includes('contractor'),
        isDealer: groups.includes('dealer'),
        isEmployee: groups.includes('employee'),
        isOperator: groups.includes('operator'),
        isPerson: groups.includes('owner'),
        isRecycler: groups.includes('recycler'),
        isImporter: groups.includes('importer'),
    } : {}),
};

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        expiredTokenLogout: state => {
            state.isAuthenticated = false;
            state.isExpired = true;
        },
        dismissLoginModal: state => {
            state.isAuthenticated = false;
            state.isExpired = false;
            state.app = APP.UNAUTHENTICATED;
        },
    },
    extraReducers: builder => {
        // There is no "logout" reducer, because the root reducer in store/createStore.js calls `forceLogout()`
        // Which returns `undefined`, so that the whole root store is cleared, and the page is refreshed.

        const { authLogin, authTeliaResponse } = API.endpoints;
        builder
            .addMatcher(
                isAnyOf(
                    authLogin.matchFulfilled,
                    authTeliaResponse.matchFulfilled,
                ),
                (state, { meta, payload }) => {
                    const { originalArgs, endpointName } = meta.arg;
                    const { app = (endpointName === 'authTeliaResponse' ? APP.PERSON : null) } = originalArgs;
                    const { access_token, refresh_token, public_key } = payload;

                    localStorage.setItem('access_token', access_token || null);
                    localStorage.setItem('refresh_token', refresh_token || null);
                    localStorage.setItem('public_key', public_key || null);
                    localStorage.setItem('app', app || null);

                    const parsed = parseJWTFromAccessToken(access_token);
                    const { first_name = '', last_name = '', groups = [] } = parsed?.user || {};
                    state.firstName = first_name;
                    state.lastName = last_name;
                    state.groups = groups;
                    state.isAuthenticated = true;

                    state.isBatteryProvider = groups.includes('provider');
                    state.isBatteryReceiver = groups.includes('receiver');
                    state.isContractor = groups.includes('contractor');
                    state.isDealer = groups.includes('dealer');
                    state.isEmployee = groups.includes('employee');
                    state.isOperator = groups.includes('operator');
                    state.isPerson = groups.includes('owner');
                    state.isRecycler = groups.includes('recycler');
                    state.isImporter = groups.includes('importer');
                    state.app = app;

                    state.isExpired = false;
                },
            );
    },
});

export const { expiredTokenLogout, dismissLoginModal } = authSlice.actions;

export default authSlice.reducer;

